import { DownOutlined } from "@ant-design/icons";
import type { RefineThemedLayoutV2HeaderProps } from "@refinedev/antd";
import { useGetIdentity, useGetLocale, useSetLocale, useTranslate } from "@refinedev/core";
import type { MenuProps } from "antd";
import { Avatar, Button, Dropdown, Layout as AntdLayout, Row, Space, theme } from "antd";
import type { PropsWithChildren } from "react";
import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCheckRoleExists } from "utils";
import { type User, UserRoleType } from "utils/types/user.type";
import { v4 } from "uuid";

import type { ModalHandle } from "..";
import { LogoutButton, useLogoutHandler } from "../auth";
import { AdditionalFillInPopup } from "./components";

const { useToken } = theme;

const isLocalDev = import.meta.env.DEV;

interface HeaderProps extends RefineThemedLayoutV2HeaderProps {
  rightOperation?: MenuProps["items"];
  isAdmin?: boolean;
}
export const Header: React.FC<PropsWithChildren<HeaderProps>> = ({ sticky, rightOperation, isAdmin }) => {
  const { token } = useToken();
  const { i18n } = useTranslation();
  const translate = useTranslate();
  const locale = useGetLocale();
  const changeLanguage = useSetLocale();
  const { data: user } = useGetIdentity<User>();
  const logoutHandler = useLogoutHandler();
  const fillInPopupRef = useRef<ModalHandle>(null);
  const currentLocale = locale();
  const navigate = useNavigate();
  const isSystemManager = useCheckRoleExists([UserRoleType.SystemManager]);
  const menuItems: MenuProps["items"] = [...(i18n.languages || [])].sort().map((lang: string) => ({
    key: lang,
    onClick: () => changeLanguage(lang),
    icon: (
      <span style={{ marginRight: 8 }}>
        <Avatar size={16} src={`/images/flags/${lang}.svg`} />
      </span>
    ),
    label: translate("locale." + lang, lang),
  }));

  const headerStyles: React.CSSProperties = {
    // backgroundColor: "white",
    backgroundColor: token.colorPrimary,
    color: token.colorWhite + " !important",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "0px 24px",
    height: "64px",
  };

  const dropDownItems = useMemo(
    () =>
      (rightOperation || [])
        .concat({
          key: v4(),
          label: "個人情報を変更",
          onClick: () => fillInPopupRef.current?.open(),
        })
        .concat({
          key: v4(),
          label: <LogoutButton />,
          onClick: ({ domEvent }) => {
            logoutHandler(domEvent as any);
          },
        }),
    [rightOperation]
  );

  if (sticky) {
    headerStyles.position = "sticky";
    headerStyles.top = 0;
    headerStyles.zIndex = 10;
  }

  return (
    <>
      <AntdLayout.Header style={headerStyles}>
        <Row justify="end">
          <Space>
            {isAdmin && isSystemManager && (
              <button
                className="h-[32px] cursor-pointer rounded-[4px] border border-solid border-[#FFFFFF] bg-[#BFDAFF] px-4 py-2 text-sm font-normal leading-[100%] text-[#1677FF]"
                onClick={() => navigate("/todo_management")}
              >
                Todoリスト
              </button>
            )}
            {isLocalDev && (
              <Dropdown
                menu={{
                  items: menuItems,
                  selectedKeys: currentLocale ? [currentLocale] : [],
                }}
              >
                <Button className="!text-white" type="text">
                  <Space>
                    <Avatar size={16} src={`/images/flags/${currentLocale}.svg`} />
                    {translate("locale." + currentLocale, currentLocale)}
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            )}
            <Dropdown menu={{ items: dropDownItems }} trigger={["click"]}>
              <Button className="!text-white" type="text">
                <Space size="middle" style={{ marginLeft: "8px" }}>
                  <strong className="!text-white">{user?.nickname || "ユーザー"}</strong>
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </Space>
        </Row>
      </AntdLayout.Header>
      <AdditionalFillInPopup ref={fillInPopupRef} />
    </>
  );
};
